/**
 * experienceProfile — how the product presents itself to a member.
 *
 *   full    — the home page with every question, inside the app shell (default)
 *   guided  — `/` renders the home page bare: orientation, the one thing the
 *             Next-Step Engine says needs them, where the plan stands, nothing else
 *
 * Stored on `workspace_memberships.experience_profile` (migration 795) and
 * changed only through `set_member_experience_profile`. Never localStorage:
 * the person this exists for cannot be asked to remember a toggle, and a
 * browser setting does not follow them to the next device.
 */

import { getSupabaseClient } from './supabaseClient';

export type ExperienceProfile = 'full' | 'guided';

export const EXPERIENCE_PROFILE_LABELS: Record<ExperienceProfile, string> = {
  full: 'Full product',
  guided: 'Guided — one thing at a time',
};

export const EXPERIENCE_PROFILE_DESCRIPTIONS: Record<ExperienceProfile, string> = {
  full: 'The home page with every question, the sidebar, and every surface.',
  guided: 'A single home page that says who they are, what needs them, and where things stand. For people who are not technical or who need every visit to start from zero.',
};

export function isExperienceProfile(value: unknown): value is ExperienceProfile {
  return value === 'full' || value === 'guided';
}

/** Reads the member's profile; `full` when the row or column is missing. */
export async function fetchExperienceProfile(workspaceId: string, userId: string): Promise<ExperienceProfile> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('workspace_memberships')
    .select('experience_profile')
    .eq('workspace_id', workspaceId)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) return 'full';
  const value = (data as { experience_profile?: unknown } | null)?.experience_profile;
  return isExperienceProfile(value) ? value : 'full';
}

/** Sets the member's profile (self, or a workspace admin). */
export async function setExperienceProfile(workspaceId: string, userId: string, profile: ExperienceProfile): Promise<void> {
  const client = getSupabaseClient();
  const { error } = await client.rpc('set_member_experience_profile', {
    p_workspace_id: workspaceId,
    p_user_id: userId,
    p_profile: profile,
  });
  if (error) throw new Error(error.message);
}
